/**
 * useResizableWidth — clamped, persisted width state for a drag-resizable panel.
 *
 * Owns three things a resizable side panel needs and a presentational handle
 * must NOT own:
 *
 * 1. **Clamping.** Every value that enters the state — the restored one, the
 *    dragged one, a keyboard nudge — passes through the same clamp. A stored
 *    width outside `[minWidth, maxWidth]` is corrected on read, never honoured,
 *    so shrinking a panel's max in a later release cannot strand a user at a
 *    width the layout no longer supports.
 * 2. **Persistence.** The width is written to `localStorage` under an
 *    injected key. Every access is wrapped: a privacy-mode browser, a disabled
 *    storage partition or a quota error falls back to `defaultWidth` and the
 *    panel still renders. Storage is a convenience here, never a dependency.
 * 3. **The pointer drag.** `startDrag` captures the pointer on the handle
 *    element, so the drag keeps tracking when the cursor leaves the thin
 *    divider — the classic "the sidebar stops following my mouse" bug. All
 *    listeners are removed on pointerup/pointercancel and on unmount.
 *
 * The hook is layout-agnostic: it deals in CSS pixels and knows nothing about
 * drawers, breakpoints or which side the panel is on beyond the `side` prop.
 *
 * @see TASK-TARN-RESIZE-HANDLE
 * @see STORY-TA-RESIZABLE-NAV AC-003 (clamping) / AC-004 (persistence)
 */
import { useCallback, useEffect, useRef, useState } from "react";

/** Pixels a single arrow-key press moves the divider. */
export const RESIZE_NUDGE_STEP_PX = 16;
/** Pixels a shifted arrow-key press moves the divider (coarse adjust). */
export const RESIZE_NUDGE_STEP_LARGE_PX = 48;

export interface UseResizableWidthOptions {
  /**
   * localStorage key the chosen width is persisted under. Must be unique per
   * panel — two panels sharing a key would fight over one stored value.
   */
  storageKey: string;
  /** Width used when nothing is stored, and the width `reset()` returns to. */
  defaultWidth: number;
  /** Lower clamp bound. Conventionally equals `defaultWidth`, so the shipped layout is the floor. */
  minWidth: number;
  /** Upper clamp bound. */
  maxWidth: number;
  /**
   * When false the hook is inert: it reports `defaultWidth`, never reads or
   * writes storage, and `startDrag` is a no-op. Used to switch resizing off in
   * responsive states that own their width (a collapsed icon rail, a mobile
   * temporary drawer).
   *
   * @default true
   */
  enabled?: boolean;
  /**
   * Which edge of the panel the handle sits on. `"right"` (default) means a
   * rightward drag widens the panel — a left-hand sidebar. `"left"` inverts it.
   *
   * @default "right"
   */
  side?: "left" | "right";
}

export interface UseResizableWidthResult {
  /** Current width in CSS pixels, already clamped. */
  width: number;
  /** True while a pointer drag is in flight — use it to suppress width transitions. */
  isDragging: boolean;
  /** Begin a pointer drag. Pass the handle's `onPointerDown` event. */
  startDrag: (event: React.PointerEvent<HTMLElement>) => void;
  /** Move the divider by `deltaPx` (positive = wider), clamped. */
  nudge: (deltaPx: number) => void;
  /** Set an absolute width, clamped. */
  setWidth: (nextWidth: number) => void;
  /** Return to `defaultWidth` and clear the stored preference. */
  reset: () => void;
  /** Echo of the clamp bounds, for the handle's ARIA value range. */
  min: number;
  max: number;
  /** Echo of `defaultWidth`. */
  defaultWidth: number;
  /** False when the hook is inert — the consumer should render no handle. */
  enabled: boolean;
}

/** Clamp `value` into `[min, max]`, tolerating a caller that swapped the bounds. */
function clamp(value: number, min: number, max: number): number {
  const low = Math.min(min, max);
  const high = Math.max(min, max);
  return Math.round(Math.min(high, Math.max(low, value)));
}

/**
 * Read the persisted width. Returns null for "nothing usable stored" — an
 * absent key, a non-numeric value, or a storage accessor that throws (Safari
 * private mode, a browser configured to block site data). Never throws.
 */
function readStoredWidth(storageKey: string): number | null {
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (raw === null) return null;
    const parsed = Number.parseFloat(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/** Persist the width. Silently gives up if storage is unavailable or full. */
function writeStoredWidth(storageKey: string, width: number): void {
  try {
    window.localStorage.setItem(storageKey, String(width));
  } catch {
    /* storage unavailable — the width still applies for this session */
  }
}

/** Remove the persisted width. Silently gives up if storage is unavailable. */
function clearStoredWidth(storageKey: string): void {
  try {
    window.localStorage.removeItem(storageKey);
  } catch {
    /* storage unavailable — nothing to clear */
  }
}

/**
 * Clamped, persisted width state for a drag-resizable panel.
 *
 * @example
 * const { width, isDragging, startDrag, nudge, reset } = useResizableWidth({
 *   storageKey: "verisa.manage.sidebar.width",
 *   defaultWidth: 260,
 *   minWidth: 260,
 *   maxWidth: 480,
 *   enabled: isDesktop,
 * });
 */
export function useResizableWidth({
  storageKey,
  defaultWidth,
  minWidth,
  maxWidth,
  enabled = true,
  side = "right",
}: UseResizableWidthOptions): UseResizableWidthResult {
  const [storedWidth, setStoredWidth] = useState<number>(() =>
    clamp(readStoredWidth(storageKey) ?? defaultWidth, minWidth, maxWidth),
  );
  const [isDragging, setIsDragging] = useState(false);

  // Drag origin, captured on pointerdown so every move is measured against the
  // press point rather than accumulating rounding error frame by frame.
  const dragOrigin = useRef<{ pointerX: number; startWidth: number } | null>(null);
  // Mirrors `storedWidth` for the pointerup handler, which is created once per
  // drag and would otherwise close over a stale value.
  const widthRef = useRef(storedWidth);
  widthRef.current = storedWidth;
  // Teardown for an in-flight drag, so an unmount mid-drag cannot leave window
  // listeners behind. Set by startDrag, cleared by its own end handler.
  const endDragRef = useRef<(() => void) | null>(null);

  // Re-clamp when the bounds change (a breakpoint flip, a config change).
  useEffect(() => {
    setStoredWidth((current) => clamp(current, minWidth, maxWidth));
  }, [minWidth, maxWidth]);

  const commit = useCallback(
    (next: number) => {
      const clamped = clamp(next, minWidth, maxWidth);
      setStoredWidth(clamped);
      writeStoredWidth(storageKey, clamped);
      return clamped;
    },
    [maxWidth, minWidth, storageKey],
  );

  const setWidth = useCallback(
    (next: number) => {
      if (!enabled) return;
      commit(next);
    },
    [commit, enabled],
  );

  const nudge = useCallback(
    (deltaPx: number) => {
      if (!enabled) return;
      commit(widthRef.current + deltaPx);
    },
    [commit, enabled],
  );

  const reset = useCallback(() => {
    if (!enabled) return;
    setStoredWidth(clamp(defaultWidth, minWidth, maxWidth));
    clearStoredWidth(storageKey);
  }, [defaultWidth, enabled, maxWidth, minWidth, storageKey]);

  const startDrag = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (!enabled) return;
      // Ignore secondary buttons — a right-click must not start a drag. Written
      // as `> 0` rather than `!== 0` on purpose: environments without a real
      // PointerEvent (jsdom) leave `button` undefined, and `!== 0` would then
      // reject every drag.
      if (event.button > 0) return;
      event.preventDefault();

      const handle = event.currentTarget;
      dragOrigin.current = { pointerX: event.clientX, startWidth: widthRef.current };
      setIsDragging(true);

      // Pointer capture keeps move events coming to the handle even when the
      // cursor runs ahead of the divider or leaves the window.
      try {
        handle.setPointerCapture(event.pointerId);
      } catch {
        /* capture unsupported (jsdom, some legacy engines) — window listeners below still work */
      }

      const direction = side === "right" ? 1 : -1;

      const handleMove = (moveEvent: PointerEvent) => {
        const origin = dragOrigin.current;
        if (!origin) return;
        const delta = (moveEvent.clientX - origin.pointerX) * direction;
        const clamped = clamp(origin.startWidth + delta, minWidth, maxWidth);
        widthRef.current = clamped;
        setStoredWidth(clamped);
      };

      const handleEnd = () => {
        if (dragOrigin.current === null) return;
        dragOrigin.current = null;
        setIsDragging(false);
        writeStoredWidth(storageKey, widthRef.current);
        window.removeEventListener("pointermove", handleMove);
        window.removeEventListener("pointerup", handleEnd);
        window.removeEventListener("pointercancel", handleEnd);
        endDragRef.current = null;
        try {
          handle.releasePointerCapture(event.pointerId);
        } catch {
          /* nothing captured */
        }
      };

      endDragRef.current = handleEnd;
      window.addEventListener("pointermove", handleMove);
      window.addEventListener("pointerup", handleEnd);
      window.addEventListener("pointercancel", handleEnd);
    },
    [enabled, maxWidth, minWidth, side, storageKey],
  );

  // Unmount mid-drag: run the in-flight drag's own teardown so its window
  // listeners go with it. Without this, closing a page while the divider is
  // held leaves a pointermove listener calling setState on a dead component.
  useEffect(() => {
    return () => {
      endDragRef.current?.();
    };
  }, []);

  return {
    width: enabled ? storedWidth : defaultWidth,
    isDragging: enabled && isDragging,
    startDrag,
    nudge,
    setWidth,
    reset,
    min: minWidth,
    max: maxWidth,
    defaultWidth,
    enabled,
  };
}

export default useResizableWidth;
